import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";

// ---- Response shapes (local — not shared via api/types.ts) ----
interface NodeStat {
  node: string;
  cpu_pct: number;
  mem_pct: number;
  disk_pct: number;
}
interface NodesResponse {
  status: "ok" | "unavailable";
  reason?: string;
  nodes: NodeStat[];
}
interface WorkloadSummary {
  pending: number;
  not_ready: number;
  restarts_24h: number;
  oomkilled_24h: number;
}
interface TopRestart {
  namespace: string;
  pod: string;
  restarts: number;
}
interface WorkloadsResponse {
  status: "ok" | "unavailable";
  reason?: string;
  summary: WorkloadSummary;
  top_restarts: TopRestart[];
}

const DESC = "Cluster nodes & Kubernetes workload health from Prometheus.";

// green <70, yellow 70-90, red >90
function barColor(pct: number): string {
  if (pct > 90) return "var(--red)";
  if (pct >= 70) return "var(--yellow)";
  return "var(--green)";
}

export function InfraTab() {
  const nodesR = useApi<NodesResponse>("/command-center/infra/nodes");
  const workR = useApi<WorkloadsResponse>("/command-center/infra/workloads");

  const loading = nodesR.loading || workR.loading;
  const error = nodesR.error || workR.error;

  if (loading) {
    return (
      <>
        <ContentHeader title="Infrastructure" description={DESC} />
        <div className="loading"><div className="loading-spinner" /><p>Querying cluster metrics…</p></div>
      </>
    );
  }
  if (error || !nodesR.data || !workR.data) {
    return (
      <>
        <ContentHeader title="Infrastructure" description={DESC} />
        <div className="empty">Could not load infrastructure metrics. Is command-center-api running?</div>
      </>
    );
  }

  const unavailable = nodesR.data.status === "unavailable" || workR.data.status === "unavailable";
  const nodes = nodesR.data.nodes ?? [];
  const summary = workR.data.summary;
  const topRestarts = workR.data.top_restarts ?? [];

  if (unavailable) {
    return (
      <>
        <ContentHeader title="Infrastructure" description={DESC} />
        <div className="empty">
          Live infrastructure metrics need <code>GRAFANA_TOKEN</code> on the API to query Prometheus.
          {(nodesR.data.reason || workR.data.reason) && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-muted)" }}>
              {nodesR.data.reason || workR.data.reason}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <ContentHeader title="Infrastructure" count={nodes.length} description={DESC} />

      <div className="cov-summary">
        <span className={`cov-pill ${summary.pending > 0 ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{summary.pending}</strong> pending
        </span>
        <span className={`cov-pill ${summary.not_ready > 0 ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{summary.not_ready}</strong> not ready
        </span>
        <span className="cov-pill">
          <span className="dot" /><strong>{summary.restarts_24h}</strong> restarts 24h
        </span>
        <span className={`cov-pill ${summary.oomkilled_24h > 0 ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{summary.oomkilled_24h}</strong> OOMKilled 24h
        </span>
      </div>

      {nodes.length === 0 ? (
        <div className="empty">No node metrics — node-exporter may not be scraped.</div>
      ) : (
        <div className="grid">
          {nodes.map((n) => <NodeCard key={n.node} node={n} />)}
        </div>
      )}

      {topRestarts.length > 0 && (
        <>
          <div className="section-header"><h2>Top restarting pods (24h)</h2><span className="count">{topRestarts.length}</span></div>
          <table className="cov-matrix">
            <thead>
              <tr><th>Namespace</th><th>Pod</th><th>Restarts</th></tr>
            </thead>
            <tbody>
              {topRestarts.map((r) => (
                <tr key={`${r.namespace}/${r.pod}`}>
                  <td><code style={{ fontFamily: "var(--font-mono)" }}>{r.namespace}</code></td>
                  <td><code style={{ fontFamily: "var(--font-mono)" }}>{r.pod}</code></td>
                  <td className={`cov-cell ${r.restarts > 0 ? "no" : "yes"}`}>{r.restarts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function NodeCard({ node }: { node: NodeStat }) {
  return (
    <div className="card">
      <div className="card-head">
        <div className="card-name" style={{ fontFamily: "var(--font-mono)" }}>{node.node}</div>
      </div>
      <Bar label="CPU" pct={node.cpu_pct} />
      <Bar label="Memory" pct={node.mem_pct} />
      <Bar label="Disk" pct={node.disk_pct} />
    </div>
  );
}

function Bar({ label, pct }: { label: string; pct: number }) {
  const width = Math.max(0, Math.min(100, pct));
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: "var(--text-muted)" }}>{label}</span>
        <span className="num" style={{ color: barColor(pct) }}>{pct}%</span>
      </div>
      <div
        aria-hidden="true"
        style={{ height: 8, borderRadius: 4, background: "var(--border, rgba(255,255,255,0.08))", overflow: "hidden" }}
      >
        <div style={{ width: `${width}%`, height: "100%", background: barColor(pct), borderRadius: 4 }} />
      </div>
    </div>
  );
}

export default InfraTab;
